// src/stores/auth.ts
//
// Estado de autenticación de la app de Seguridad. Reusa el login del admin
// (/admin?action=login) → el usuario es un admin_usuario con rol de seguridad.
// Persistido en localStorage para no re-loguear en cada turno.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Roles que pueden operar la app de seguridad.
export const ROLES_SEGURIDAD = [
  'superadmin',
  'admin',
  'jefe_seguridad',
  'agente_seguridad',
  'radio_operador',
] as const;

export type RolSeguridad = (typeof ROLES_SEGURIDAD)[number] | string;

export type SegUser = {
  id: number;
  rol: RolSeguridad;
  nombre: string;
  apellido: string;
  email: string;
  municipio_id: number | null;
  municipio_nombre: string | null;
  destacamento_id: number | null;
  destacamento_nombre: string | null;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: SegUser | null;
  setSession: (accessToken: string, refreshToken: string, user: SegUser) => void;
  logout: () => void;
};

/** ¿El rol puede entrar a la app de seguridad? */
export function esRolSeguridad(rol: string | undefined | null): boolean {
  return !!rol && (ROLES_SEGURIDAD as readonly string[]).includes(rol);
}

/** ¿Es un rol de gestión (central/jefe) vs agente de calle? */
export function esCentral(rol: string | undefined | null): boolean {
  return rol === 'superadmin' || rol === 'admin' || rol === 'jefe_seguridad' || rol === 'radio_operador';
}

/** ¿Puede gestionar el personal de la comisaría? (mismo criterio que auth_turno_admin del backend) */
export function esGestorPersonal(rol: string | undefined | null): boolean {
  return rol === 'superadmin' || rol === 'admin' || rol === 'jefe_seguridad';
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setSession: (accessToken, refreshToken, user) =>
        set({ accessToken, refreshToken, user }),
      logout: () => set({ accessToken: null, refreshToken: null, user: null }),
    }),
    {
      name: 'ciudadapp-seguridad-auth',
    }
  )
);
